import { useState, useCallback, useEffect, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { ParsePreview } from './ParsePreview';
import { ManualEntryForm } from './ManualEntryForm';
import { PNRLookupForm } from './PNRLookupForm';
import type { BookingFormData, ParsedLookupResult } from '../../lib/types';
import { sectionLabelVariants } from '@perch/ui';
import { SUPABASE_FUNCTIONS_URL } from '../../lib/supabase';
import { getEmailProvider, type EmailProvider } from '../auth/emailUtils';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import type { Session, User } from '@supabase/supabase-js';

/*
 * Unified Add Booking Modal — 3 input methods, 1 convergence point.
 *
 *   ┌──────────┬──────────┬──────────┐
 *   │ Forward  │ Look Up  │ Manual   │
 *   │  Email   │  (PNR)   │  Entry   │
 *   └────┬─────┴────┬─────┴────┬─────┘
 *        │          │          │
 *        └──────────┴──────────┘
 *                   │
 *            ParsePreview (editable boarding pass)
 *                   │
 *            "Track This Flight" → save
 */

const TRACK_EMAIL = 'track@perchtravel.com';
const TAB_STORAGE_KEY = 'perch_add_booking_tab';

type TabId = 'email' | 'lookup' | 'manual';

interface AddBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session | null;
  user?: User | null;
  onSuccess: (isAward: boolean, amount: number) => void;
  /** Called when PNR lookup is submitted (async). Modal closes, dashboard shows pending card. */
  onLookupSubmitted?: () => void;
  /**
   * When provided, the modal opens straight into ParsePreview with this data
   * pre-filled (skipping the tab picker). Used when confirming a pending
   * lookup from a low-confidence email parse.
   */
  initialPreview?: ParsedLookupResult | null;
  /**
   * Fires AFTER a successful add-booking create and BEFORE the modal closes.
   * Use this to promote the originating pending_lookup to status='confirmed'.
   * Runs synchronously relative to handleConfirm so the parent can capture
   * the pending lookup id before any close-side state resets.
   */
  onBookingCreated?: () => void;
}

const INITIAL_FORM_DATA: BookingFormData = {
  pnr: '',
  airline_code: '',
  booking_date: new Date().toISOString().slice(0, 10),
  total_price_paid: 0,
  currency: 'USD',
  is_award_booking: false,
  fare_class: '',
  cabin_class: 'economy',
  fare_basis_code: '',
  segments: [],
};

function getInitialTab(): TabId {
  try {
    const stored = localStorage.getItem(TAB_STORAGE_KEY);
    if (stored === 'email' || stored === 'lookup' || stored === 'manual') return stored;
  } catch { /* noop */ }
  return 'email';
}

function saveTab(tab: TabId) {
  try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch { /* noop */ }
}

export function AddBookingModal({
  isOpen,
  onClose,
  session,
  user,
  onSuccess,
  onLookupSubmitted,
  initialPreview,
  onBookingCreated,
}: AddBookingModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab);
  const [previewData, setPreviewData] = useState<ParsedLookupResult | null>(null);
  const [formData, setFormData] = useState<BookingFormData>(INITIAL_FORM_DATA);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track initialPreview across renders so we only seed once per open cycle.
  const lastSeededRef = useRef<ParsedLookupResult | null>(null);
  useEffect(() => {
    if (isOpen && initialPreview && lastSeededRef.current !== initialPreview) {
      setPreviewData(initialPreview);
      lastSeededRef.current = initialPreview;
    }
    if (!isOpen) {
      lastSeededRef.current = null;
    }
  }, [isOpen, initialPreview]);

  const resetState = useCallback(() => {
    setPreviewData(null);
    setFormData(INITIAL_FORM_DATA);
    setError('');
    setIsSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleTabChange = useCallback((tab: TabId) => {
    setActiveTab(tab);
    saveTab(tab);
    setError('');
  }, []);

  // Manual entry → preview conversion
  const handleManualReview = useCallback(() => {
    const parsed: ParsedLookupResult = {
      pnr: formData.pnr,
      airline_code: formData.airline_code,
      booking_date: formData.booking_date,
      total_price_paid: formData.total_price_paid,
      currency: formData.currency,
      is_award_booking: formData.is_award_booking,
      points_program: formData.points_program,
      points_used: formData.points_used,
      cabin_class: formData.cabin_class,
      segments: formData.segments.map((seg) => ({
        segment_number: seg.segment_number,
        carrier_code: seg.carrier_code,
        flight_number: seg.flight_number,
        origin: seg.origin,
        destination: seg.destination,
        departure_datetime: seg.departure_datetime,
        arrival_datetime: seg.arrival_datetime,
        cabin_code: seg.cabin_code,
      })),
    };
    setPreviewData(parsed);
  }, [formData]);

  // Save confirmed booking via existing add-booking edge function
  const handleConfirm = useCallback(async () => {
    if (!session || !previewData) return;
    setIsSubmitting(true);
    setError('');

    // Pre-submit datetime sanity check. Duplicates the server-side
    // gate in supabase/functions/_shared/segment-validation.ts but
    // short-circuits before a wasted network round-trip and surfaces
    // the error inline. An earlier version of this function silently
    // fell back `arrival -> departure` when arrival was empty, which
    // produced arrival == departure and tripped the Postgres
    // `valid_datetime` CHECK (Sentry PERCH-EDGE-FUNCTIONS-1C). No
    // silent fallbacks now — if data is missing we refuse to submit.
    const invalidSegmentIdx = previewData.segments.findIndex((seg) => {
      if (!seg.departure_datetime || !seg.arrival_datetime) return true;
      const dep = new Date(seg.departure_datetime).getTime();
      const arr = new Date(seg.arrival_datetime).getTime();
      if (Number.isNaN(dep) || Number.isNaN(arr)) return true;
      return arr <= dep;
    });
    if (invalidSegmentIdx >= 0) {
      setError(
        `Flight ${invalidSegmentIdx + 1}: arrival time must be after departure time. ` +
          `For overnight flights, use the next day's date for arrival.`,
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const payload: BookingFormData = {
        pnr: previewData.pnr,
        airline_code: previewData.airline_code,
        booking_date: previewData.booking_date || new Date().toISOString().slice(0, 10),
        total_price_paid: previewData.total_price_paid || 0,
        currency: previewData.currency || 'USD',
        is_award_booking: previewData.is_award_booking || false,
        cabin_class: previewData.cabin_class || 'economy',
        is_changeable: formData.is_changeable,
        points_program: previewData.points_program,
        points_used: previewData.points_used,
        segments: previewData.segments.map((seg, i) => ({
          segment_number: seg.segment_number || i + 1,
          carrier_code: seg.carrier_code,
          flight_number: seg.flight_number,
          origin: seg.origin,
          destination: seg.destination,
          departure_datetime: new Date(seg.departure_datetime).toISOString(),
          // Validated above by invalidSegmentIdx — arrival is guaranteed
          // truthy and strictly greater than departure by the time we
          // reach this map. The non-null assertion reflects that runtime
          // guarantee; TS can't narrow across the findIndex closure.
          arrival_datetime: new Date(seg.arrival_datetime!).toISOString(),
          cabin_code: seg.cabin_code || 'Y',
          booking_class: 'Y',
        })),
      };

      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/add-booking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success || result.booking_id) {
        // Fire callbacks BEFORE closing so parent state (e.g. the pending
        // lookup id we're promoting) is still valid when they run.
        onBookingCreated?.();
        onSuccess(payload.is_award_booking, payload.total_price_paid);
        handleClose();
      } else {
        throw new Error(result.error || 'Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [session, previewData, handleClose, onSuccess, onBookingCreated, formData.is_changeable]);

  // Detect email provider for "Find My Email" links
  const emailProvider = user?.email ? getEmailProvider(user.email) : 'generic';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add a Booking" size="md">
      <div className="space-y-4 relative">
        {/* Tab bar */}
        {!previewData && (
          <div role="tablist" className="flex border-b border-stone-200/60 dark:border-stone-700/50 -mx-1">
            <TabButton
              id="email"
              label="Forward Email"
              icon={<EnvelopeIcon />}
              active={activeTab === 'email'}
              onClick={() => handleTabChange('email')}
            />
            <TabButton
              id="lookup"
              label="Look Up"
              icon={<SearchIcon />}
              active={activeTab === 'lookup'}
              onClick={() => handleTabChange('lookup')}
            />
            <TabButton
              id="manual"
              label="Manual"
              icon={<PencilIcon />}
              active={activeTab === 'manual'}
              onClick={() => handleTabChange('manual')}
            />
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200/50 dark:border-red-800/30">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Preview mode — all tabs converge here */}
        {previewData ? (
          <ParsePreview
            data={previewData}
            onChange={setPreviewData}
            onConfirm={handleConfirm}
            onBack={() => setPreviewData(null)}
            isSubmitting={isSubmitting}
          />
        ) : (
          /* Tab content */
          <div role="tabpanel">
            {activeTab === 'email' && (
              <EmailForwardTab emailProvider={emailProvider} onClose={handleClose} />
            )}
            {activeTab === 'lookup' && (
              <PNRLookupForm
                session={session}
                onClose={handleClose}
                onLookupSubmitted={onLookupSubmitted}
              />
            )}
            {activeTab === 'manual' && (
              <ManualEntryForm
                formData={formData}
                onFormDataChange={setFormData}
                onReview={handleManualReview}
                onBack={handleClose}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// --- Tab Button ---

function TabButton({ id, label, icon, active, onClick }: {
  id: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      role="tab"
      id={`tab-${id}`}
      aria-selected={active}
      aria-controls={`panel-${id}`}
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors border-b-2 ${
        active
          ? 'text-stone-900 dark:text-stone-100 border-stone-900 dark:border-stone-100'
          : 'text-stone-400 dark:text-stone-500 border-transparent hover:text-stone-600 dark:hover:text-stone-300'
      }`}
    >
      <span className="hidden sm:inline">{icon}</span>
      {label}
    </button>
  );
}

// --- Email Forward Tab ---

/** Provider-specific search URLs to find airline confirmation emails. */
const FIND_EMAIL_URLS: Partial<Record<EmailProvider, string>> = {
  gmail: 'https://mail.google.com/mail/u/0/#search/from%3A(delta+OR+united+OR+american)+confirmation',
  outlook: 'https://outlook.live.com/mail/0/search?query=airline%20confirmation',
  yahoo: 'https://mail.yahoo.com/search/keyword=airline%20confirmation',
};

function EmailForwardTab({ emailProvider, onClose }: {
  emailProvider: EmailProvider;
  onClose: () => void;
}) {
  const { copied, copy: copyEmail } = useCopyToClipboard(TRACK_EMAIL);
  const findEmailUrl = FIND_EMAIL_URLS[emailProvider];

  return (
    <div className="space-y-6 py-2">
      <div className="text-center space-y-4">
        <div className="w-14 h-14 bg-stone-100 dark:bg-stone-800 rounded-2xl flex items-center justify-center mx-auto">
          <EnvelopeIcon className="w-7 h-7 text-stone-600 dark:text-stone-400" />
        </div>

        <div>
          <h3 className="text-lg font-medium text-stone-900 dark:text-stone-100 mb-1">
            Forward Your Confirmation Email
          </h3>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            Forward your airline confirmation email and we'll add it automatically.
          </p>
        </div>

        <button
          onClick={copyEmail}
          className="group w-full flex items-center justify-between px-5 py-4 bg-stone-50 dark:bg-stone-800 border-2 border-stone-200/60 dark:border-stone-700/50 rounded-2xl hover:border-stone-300 dark:hover:border-stone-600 transition-colors"
        >
          <span className="font-mono text-lg text-stone-900 dark:text-stone-100 tracking-wide">
            {TRACK_EMAIL}
          </span>
          <span className={`${sectionLabelVariants({ variant: copied ? 'emerald' : 'default' })} transition-colors ${
            !copied ? 'group-hover:text-stone-600 dark:group-hover:text-stone-300' : ''
          }`}>
            {copied ? 'Copied!' : 'Copy'}
          </span>
        </button>

        {findEmailUrl && (
          <a
            href={findEmailUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
          >
            <SearchIcon className="w-3.5 h-3.5" />
            Find My Confirmation Email
          </a>
        )}
      </div>

      <div className="pt-2 border-t border-stone-200/60 dark:border-stone-700/50">
        <p className="text-sm font-medium text-stone-700 dark:text-stone-300">
          Works with Delta, United, and American.
        </p>
        <p className="text-xs text-stone-400 dark:text-stone-500 mt-1">
          More airlines on the way.
        </p>
      </div>

      <button
        onClick={onClose}
        className="w-full py-3 border border-stone-200/60 dark:border-stone-700/50 text-stone-600 dark:text-stone-400 rounded-full font-medium hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
      >
        Done
      </button>
    </div>
  );
}

// --- Icons ---

function EnvelopeIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  );
}

function SearchIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  );
}

function PencilIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
    </svg>
  );
}
